import React, { useEffect, useState, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Compact pill that shows the current user's remaining daily LLM quota.
// Hides itself for admin/super_admin (exempt) and while loading.
export default function LlmQuotaBadge() {
  const [quota, setQuota] = useState(null);
  const lastDateRef = useRef(null);

  const fetchQuota = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/llm-quota`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.ok) {
        const next = await r.json();
        // Midnight-reset toast — fires the moment the server-side date
        // rolls over and our count drops back down.  Skips the very first
        // fetch of a session (nothing to compare against yet).
        if (
          lastDateRef.current &&
          next.date !== lastDateRef.current &&
          next.count === 0 &&
          !next.exempt
        ) {
          const seen = sessionStorage.getItem(`quota_reset_toast_${next.date}`);
          if (!seen) {
            toast.success(`✨ You are back to ${next.limit} AI actions today. Good luck!`, { duration: 6000 });
            sessionStorage.setItem(`quota_reset_toast_${next.date}`, '1');
          }
        }
        lastDateRef.current = next.date;
        setQuota(next);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchQuota();
    // Refresh every 60s so it stays roughly accurate while the tab is open.
    const iv = setInterval(fetchQuota, 60000);
    return () => clearInterval(iv);
  }, []);

  if (!quota || quota.exempt) return null;

  const pct = quota.count / quota.limit;
  const colour =
    pct >= 1 ? 'bg-red-500/15 text-red-500 border-red-500/40' :
    pct >= 0.7 ? 'bg-amber-500/15 text-amber-500 border-amber-500/40' :
    'bg-emerald-500/15 text-emerald-500 border-emerald-500/40';

  return (
    <Badge
      variant="outline"
      className={`h-7 gap-1 text-[10px] uppercase tracking-wide ${colour}`}
      data-testid="llm-quota-badge"
      title={`You have used ${quota.count} of ${quota.limit} AI-assisted actions today. Resets at midnight UTC.`}
    >
      <Sparkles className="w-3 h-3" />
      <span>AI · {quota.remaining}/{quota.limit}</span>
    </Badge>
  );
}
